/**
 * The signed-in person's display identity, for the account menu.
 *
 * Read from `core-user-management` — **not** from the token. The token carries
 * identity claims, but the display name and the photo are user-management's
 * record and change without a new token being issued (FiveOX ADR-0006:
 * permissions are never in the token, and the same discipline keeps mutable
 * profile data out of it).
 *
 * A failure here renders initials instead of a photo and nothing else breaks,
 * which is why it is not part of the access gate.
 */

import { useEffect, useState } from 'react'

import { USERS_API } from './config'
import { session } from './session'

export interface Me {
  name: string | null
  email: string | null
  pictureUrl: string | null
}

export function useMe(): Me | null {
  const current = session.useSession()
  const [me, setMe] = useState<Me | null>(null)

  useEffect(() => {
    if (!current) {
      setMe(null)
      return
    }
    let cancelled = false
    // `${USERS_API}/me`, **not** `/api/v1/users/me`. The edge maps
    // `/api/users/me` → user-service's `/api/v1/me`, so the version and the
    // resource are already in the prefix. The wrong path 404s, which the
    // catch below swallowed — so the avatar silently fell back to initials
    // and the account menu had no name or email. Found by looking at the nav
    // bar, not by a test (2026-08-25).
    session
      .authed(`${USERS_API}/me`)
      .then((res) => {
        if (!res.ok) {
          // The other half of the silence: a 404 is not an exception, so the
          // catch below never saw it either.
          console.warn('[gst] profile read refused', res.status, `${USERS_API}/me`)
          return null
        }
        return res.json()
      })
      .then((body) => {
        if (cancelled || !body) return
        const data = body.data ?? body
        setMe({
          name: data.full_name ?? data.display_name ?? null,
          email: data.email ?? null,
          pictureUrl: data.picture_url ?? null,
        })
      })
      .catch((err) => {
        // Profile is decoration — it must never fail the app. But swallowing
        // it *silently* is what let a wrong URL live: log it, so the next
        // person sees the 404 instead of a nav bar that merely looks plain.
        console.warn('[gst] could not load profile for the account menu', err)
      })
    return () => {
      cancelled = true
    }
  }, [current?.userId])

  return me
}
